import { useState } from "react";
import { Link } from "react-router-dom";
import { TextInput, SelectField, CountryCodeSelect } from "../components/Inputs";
import { TIMEZONES } from "../utils/timezones";
import { useLoaderStore } from "../context";
import { useAlert, useApi, useProfile, invalidateProfile } from "../hooks";
import { API } from "../utils/constants";

/**
 * Profile — Self-service profile page (SPR-008 FE-3).
 *
 * Reachable from the top-nav user menu. Edits the authenticated user's own
 * basic props via GET/PUT /api/profile (CON-002 §1A):
 *   username, firstName, lastName, email, countryCode + mobilePhoneNumber, timezone.
 *
 * - 409 (duplicate username/email) surfaces an inline error + toast.
 * - Change-password is reachable from here (links to /change-password).
 */
export default function Profile() {
  const alert = useAlert();
  const { put } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { profile, isLoading, error, mutate } = useProfile();

  const [form, setForm] = useState(null);
  const [conflictError, setConflictError] = useState("");

  // Hydrate the editable form from the fetched profile once it arrives. Keyed
  // off profile identity so a revalidation refreshes the baseline.
  const view = form ?? {
    username: profile?.username ?? "",
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    email: profile?.email ?? "",
    countryCode: profile?.countryCode ?? "",
    mobilePhoneNumber: profile?.mobilePhoneNumber ?? "",
    timezone: profile?.timezone ?? "",
  };

  const setField = (key) => (e) =>
    setForm({ ...view, [key]: e.target.value });

  // For inputs that emit a raw value (e.g. CountryCodeSelect) rather than an event.
  const setValue = (key) => (val) => setForm({ ...view, [key]: val });

  const validate = () => {
    if (!view.username.trim()) { alert("Validation", "Username is required.", "warning"); return false; }
    if (!view.firstName.trim()) { alert("Validation", "First name is required.", "warning"); return false; }
    if (!view.lastName.trim()) { alert("Validation", "Last name is required.", "warning"); return false; }
    if (!view.email.trim()) { alert("Validation", "Email is required.", "warning"); return false; }
    return true;
  };

  const handleSave = async () => {
    setConflictError("");
    if (!validate()) return;
    setShowLoader(true);

    try {
      const updated = await put(API.PROFILE.UPDATE, {
        username: view.username.trim(),
        firstName: view.firstName.trim(),
        lastName: view.lastName.trim(),
        email: view.email.trim(),
        countryCode: view.countryCode.trim() || null,
        mobilePhoneNumber: view.mobilePhoneNumber.trim() || null,
        timezone: view.timezone.trim() || null,
      });
      await mutate(updated, { revalidate: false });
      await invalidateProfile();
      setForm(null); // re-baseline from the refreshed profile
      alert("Saved", "Your profile has been updated.", "success");
    } catch (err) {
      if (err?.status === 409) {
        const msg = err?.data?.detail || "That username or email is already in use.";
        setConflictError(msg);
        alert("Conflict", msg, "error");
      } else {
        alert("Error", err?.data?.detail || "Failed to save your profile.", "error");
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <main data-testid="profile-page" className="p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="font-display text-4xl text-charcoal uppercase">My Profile</h1>
        <p className="font-body text-charcoal/60 text-sm mt-1">
          Update your account details and contact information.
        </p>
      </div>

      {isLoading && <p className="font-body text-charcoal/60">Loading profile…</p>}
      {error && <p className="font-body text-rust">Unable to load your profile.</p>}

      {!isLoading && !error && (
        <div className="bg-white border-2 border-charcoal rounded-2xl shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6 space-y-4">
          <TextInput label="Username *" name="profile-username" value={view.username} onChange={setField("username")} />
          <TextInput label="First Name *" name="profile-first-name" value={view.firstName} onChange={setField("firstName")} />
          <TextInput label="Last Name *" name="profile-last-name" value={view.lastName} onChange={setField("lastName")} />
          <TextInput label="Email *" name="profile-email" value={view.email} onChange={setField("email")} />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <CountryCodeSelect name="profile-country-code" value={view.countryCode} onChange={setValue("countryCode")} />
            <div className="sm:col-span-2">
              <TextInput label="Mobile Number" name="profile-mobile" value={view.mobilePhoneNumber} onChange={setField("mobilePhoneNumber")} placeholder="612-555-0100" />
            </div>
          </div>

          <SelectField label="Timezone" name="profile-timezone" value={view.timezone} onChange={setField("timezone")} options={TIMEZONES} placeholder="Select a timezone…" />

          {conflictError && (
            <p className="font-body text-sm text-rust" data-testid="profile-conflict-error">
              {conflictError}
            </p>
          )}

          <div className="flex items-center justify-between pt-2">
            <Link
              to="/change-password"
              className="font-heading tracking-wider text-sm text-teal hover:text-teal/70 transition-colors"
              data-testid="profile-change-password-link"
            >
              CHANGE PASSWORD
            </Link>
            <button
              onClick={handleSave}
              className="bg-rust text-white font-heading tracking-widest px-6 py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
              data-testid="profile-save-button"
            >
              SAVE CHANGES
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
